import type { UnknownAction } from '@reduxjs/toolkit';

import { hasAsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import * as actions from './actions';
import {
  mkShare,
  SHARES_REDUCER_KEY,
  type Share,
  type ShareInput,
  type ShareProps,
  type SharesStore,
  type SharesStoreProps,
} from './types';

const mkSharesStore = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<ShareProps>({
  resourceName: 'SHARES',
});

const toShare = (share: ShareInput): Share =>
  'merge' in share && typeof share.merge === 'function' ? share : mkShare(share);

function reducer(state: SharesStore = mkSharesStore(), action: UnknownAction): SharesStore {
  switch (true) {
    case actions.getShares.match(action):
      return state.set('isLoading', true);

    case actions.getSharesSuccess.match(action):
      return resourceUpserters.upsertResources<ShareProps>(state, action.payload);

    case actions.getSharesFailure.match(action):
      return resourceUpserters.completeWithError<ShareProps>(state, action.payload);

    case actions.createShare.match(action):
      return resourceUpserters.upsertResource<ShareProps>(state, toShare(action.payload));

    case actions.createShareSuccess.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo<ShareProps, SharesStoreProps>(
        state,
        { ...action, payload: toShare(action.payload) },
        actions.deleteShare,
      );

    case actions.updateShare.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo<ShareProps, SharesStoreProps>(
        state,
        { ...action, payload: toShare(action.payload) },
        actions.updateShare,
      );

    case actions.updateShareSuccess.match(action):
      if (hasAsyncDispatch(action)) {
        action.asyncDispatch(actions.getShares());
      }
      return resourceUpserters.upsertResource<ShareProps>(state, toShare(action.payload));

    case actions.deleteShare.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo<ShareProps, SharesStoreProps>(
        state,
        { ...action, payload: toShare(action.payload) },
        actions.createShare,
      );

    case actions.deleteShareSuccess.match(action):
      return resourceUpserters.upsertResource<ShareProps>(state, toShare(action.payload));

    case actions.createShareFailure.match(action):
    case actions.updateShareFailure.match(action):
    case actions.deleteShareFailure.match(action):
      if (hasAsyncDispatch(action)) {
        action.asyncDispatch(actions.getShares());
      }
      return resourceUpserters.resetResources<ShareProps, SharesStoreProps>(state, action.payload);

    default:
      return state;
  }
}

export const REDUCER_KEY = SHARES_REDUCER_KEY;
export default reducer;
